import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation } from '@react-navigation/native';
import { MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  ActivityIndicator,
  Animated,
  Easing,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from 'react-native';
import * as yup from 'yup';
import * as yupLocale from 'yup';
import type { LoginRequest } from '../../api/types';
import { BrandLogo, Checkbox, GoogleSignInButton, IconTextField } from '../../components';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import { RedirectManager } from '../../utils/storage';
import {
  clearError,
  loginUser,
  requestVerificationToken,
  selectAuthError,
  selectAuthLoading,
  selectValidationErrors,
} from './authSlice';

void yupLocale; // keep the import side-effect tolerant for future localization

const schema = yup.object({
  username: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
});

const DESKTOP_BREAKPOINT = 1024;

export const LoginScreen: React.FC = () => {
  const navigation = useNavigation();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);

  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;

  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: yupResolver(schema),
    defaultValues: { username: '', password: '' },
  });

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearOnType = () => {
    if (error || validationErrors) dispatch(clearError());
  };

  const onSubmit = async (data: LoginRequest) => {
    dispatch(clearError());
    const result = await dispatch(loginUser(data));

    if (loginUser.fulfilled.match(result)) {
      if (result.payload.requires_verification) return;
      const redirectUrl = await RedirectManager.getAndClearRedirectUrl();
      if (redirectUrl) RedirectManager.redirectToUrl(redirectUrl);
    } else if (loginUser.rejected.match(result)) {
      const err = result.payload as any;
      const isVerifErr =
        err?.message?.toLowerCase().includes('verification') ||
        err?.message?.toLowerCase().includes('verify') ||
        err?.message?.toLowerCase().includes('unverified') ||
        err?.code === 'EMAIL_NOT_VERIFIED' ||
        err?.status === 403 ||
        err?.message?.toLowerCase().includes('email');
      if (isVerifErr) {
        await dispatch(requestVerificationToken(data));
      }
    }
  };

  // On web, `GoogleSignInButton` self-dispatches `loginWithGoogle` against
  // GIS and shows its own error toasts. On native it still renders the
  // "coming soon" toast internally until the expo-auth-session path ships.
  // Either way the screen no longer needs an onPress handler here.

  const errorText = (() => {
    if (typeof error === 'string' && error.length > 0) return error;
    if (validationErrors) {
      if (typeof validationErrors === 'string') return validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const first = validationErrors[0];
        return typeof first === 'string' ? first : (first as any)?.message ?? null;
      }
    }
    return null;
  })();

  return (
    <View style={styles.page}>
      <DecorativeBlurs />
      <KeyboardAvoidingView
        style={styles.kav}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[styles.scroll, isDesktop && styles.scrollDesktop]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View
            style={[
              styles.shell,
              isDesktop ? styles.shellDesktop : styles.shellMobile,
            ]}
          >
            {isDesktop && <HeroPanel />}

            <View style={[styles.formCol, isDesktop && styles.formColDesktop]}>
              {!isDesktop && <MobileBrand />}

              <View style={styles.formInner}>
                <View style={styles.headingBlock}>
                  <Text style={[styles.h1, isDesktop && styles.h1Desktop]}>
                    Welcome Back
                  </Text>
                  <Text style={styles.subtitle}>
                    Access your household inventory dashboard.
                  </Text>
                </View>

                <GoogleSignInButton />

                <Divider label="Or email login" />

                <View style={styles.form}>
                  <Controller
                    control={control}
                    name="username"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <IconTextField
                        label="Email Address"
                        leftIcon="mail"
                        placeholder="name@example.com"
                        value={value}
                        onChangeText={(t) => {
                          onChange(t);
                          clearOnType();
                        }}
                        onBlur={onBlur}
                        error={
                          errors.username?.message
                            ? String(errors.username.message)
                            : undefined
                        }
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        textContentType="emailAddress"
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="password"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <IconTextField
                        label="Password"
                        leftIcon="lock"
                        rightIcon={showPassword ? 'visibility-off' : 'visibility'}
                        onRightIconPress={() => setShowPassword((v) => !v)}
                        placeholder="••••••••"
                        value={value}
                        onChangeText={(t) => {
                          onChange(t);
                          clearOnType();
                        }}
                        onBlur={onBlur}
                        error={
                          errors.password?.message
                            ? String(errors.password.message)
                            : undefined
                        }
                        secureTextEntry={!showPassword}
                        autoCapitalize="none"
                        autoComplete="password"
                        textContentType="password"
                        labelAccessory={
                          <Pressable
                            onPress={() =>
                              (navigation as any).navigate('ForgotPassword')
                            }
                            hitSlop={8}
                          >
                            <Text style={styles.forgotLink}>Forgot password?</Text>
                          </Pressable>
                        }
                      />
                    )}
                  />

                  <Checkbox
                    value={rememberDevice}
                    onValueChange={setRememberDevice}
                    label="Remember this device"
                  />

                  {errorText ? (
                    <View style={styles.errorBanner}>
                      <MaterialIcons
                        name="error-outline"
                        size={18}
                        color={fpColors.error}
                      />
                      <Text style={styles.errorBannerText}>{errorText}</Text>
                    </View>
                  ) : null}

                  <PrimaryCTA
                    label="Sign In to Dashboard"
                    onPress={handleSubmit(onSubmit)}
                    loading={isLoading}
                  />
                </View>

                <Text style={styles.footerText}>
                  Don&apos;t have an account yet?{' '}
                  <Text
                    style={styles.footerLink}
                    onPress={() => (navigation as any).navigate('Register')}
                  >
                    Create an account
                  </Text>
                </Text>

                <View style={styles.supportRow}>
                  <SupportLink icon="help-outline" label="Help Center" />
                  <SupportLink icon="verified-user" label="Privacy Policy" />
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

/* ——————————————————————————————————————————————————————————————
 * Local presentational pieces. Kept in-file because they are Login-specific.
 * Extract to `src/components/` when Register/ForgotPassword need them.
 * ———————————————————————————————————————————————————————————— */

const MobileBrand: React.FC = () => (
  <View style={styles.mobileBrand}>
    <View style={styles.mobileBrandMark}>
      <BrandLogo variant="mark" tone="mono" tint={fpColors.onPrimaryContainer} height={36} />
    </View>
    <BrandLogo variant="horizontal" height={26} tint={fpColors.primary} tone="mono" />
  </View>
);

const HeroPanel: React.FC = () => (
  <View style={styles.hero}>
    <View style={styles.heroOverlay} pointerEvents="none" />
    <View style={styles.heroContent}>
      <View style={styles.heroBrandRow}>
        <BrandLogo
          variant="horizontal"
          height={32}
          tone="mono"
          tint={fpColors.onPrimaryContainer}
        />
      </View>
      <Text style={styles.heroHeadline}>
        Master your home&apos;s{'\n'}
        <Text style={styles.heroHeadlineAccent}>digital soul.</Text>
      </Text>
    </View>

    <View style={styles.testimonial}>
      <Text style={styles.testimonialQuote}>
        &ldquo;The simplest way to track, organize, and protect everything you
        own. Professional-grade inventory for the modern household.&rdquo;
      </Text>
      <View style={styles.testimonialMeta}>
        <View style={styles.avatar}>
          <MaterialIcons
            name="person"
            size={20}
            color={fpColors.onSecondaryFixed}
          />
        </View>
        <View>
          <Text style={styles.testimonialName}>A Founsi AI customer</Text>
          <Text style={styles.testimonialTitle}>Verified household</Text>
        </View>
      </View>
    </View>
  </View>
);

const Divider: React.FC<{ label: string }> = ({ label }) => (
  <View style={styles.dividerRow}>
    <View style={styles.dividerLine} />
    <Text style={styles.dividerLabel}>{label}</Text>
    <View style={styles.dividerLine} />
  </View>
);

interface PrimaryCTAProps {
  label: string;
  onPress: () => void;
  loading?: boolean;
  disabled?: boolean;
}
const PrimaryCTA: React.FC<PrimaryCTAProps> = ({
  label,
  onPress,
  loading,
  disabled,
}) => {
  const scale = useRef(new Animated.Value(1)).current;
  const pressIn = () =>
    Animated.timing(scale, {
      toValue: 0.97,
      duration: 90,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();
  const pressOut = () =>
    Animated.timing(scale, {
      toValue: 1,
      duration: 140,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();

  return (
    <Animated.View style={{ transform: [{ scale }] }}>
      <Pressable
        onPress={onPress}
        onPressIn={pressIn}
        onPressOut={pressOut}
        disabled={disabled || loading}
        style={[styles.cta, (disabled || loading) && styles.ctaDisabled]}
        accessibilityRole="button"
      >
        {loading ? (
          <ActivityIndicator color={fpColors.onPrimary} />
        ) : (
          <>
            <Text style={styles.ctaLabel}>{label}</Text>
            <MaterialIcons
              name="arrow-forward"
              size={20}
              color={fpColors.onPrimary}
            />
          </>
        )}
      </Pressable>
    </Animated.View>
  );
};

const SupportLink: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
}> = ({ icon, label }) => (
  <Pressable style={styles.supportLink} hitSlop={8}>
    <MaterialIcons name={icon} size={14} color={fpColors.outline} />
    <Text style={styles.supportLabel}>{label}</Text>
  </Pressable>
);

const DecorativeBlurs: React.FC = () => (
  <>
    <View
      pointerEvents="none"
      style={[styles.blur, styles.blurTopLeft]}
    />
    <View
      pointerEvents="none"
      style={[styles.blur, styles.blurBottomRight]}
    />
  </>
);

/* ——— styles ——— */

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: fpColors.background,
  },
  kav: { flex: 1 },
  scroll: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
  },
  scrollDesktop: {
    padding: 32,
  },

  shell: {
    width: '100%',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.08,
    shadowRadius: 30,
    elevation: 6,
  },
  shellMobile: {
    maxWidth: 440,
  },
  shellDesktop: {
    maxWidth: 1120,
    minHeight: 700,
    flexDirection: 'row',
  },

  hero: {
    flex: 1,
    backgroundColor: fpColors.primaryContainer,
    padding: 48,
    justifyContent: 'space-between',
    overflow: 'hidden',
    minHeight: 700,
  },
  heroOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: '#0000',
    // subtle radial via layered box-shadow-ish; RN can't do radial gradient
    // natively, so this is a flat overlay to keep legibility.
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
  },
  heroBrandRow: {
    marginBottom: 32,
  },
  heroHeadline: {
    fontFamily: 'Inter_700Bold',
    fontSize: 46,
    lineHeight: 52,
    color: fpColors.onPrimaryContainer,
    letterSpacing: -0.5,
  },
  heroHeadlineAccent: {
    color: fpColors.primaryFixed,
  },
  testimonial: {
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
    borderRadius: 12,
    padding: 20,
  },
  testimonialQuote: {
    fontFamily: 'Inter_500Medium',
    fontSize: 17,
    lineHeight: 26,
    fontStyle: 'italic',
    color: `${fpColors.onPrimaryContainer}E6`,
  },
  testimonialMeta: {
    marginTop: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: fpColors.secondaryFixed,
    justifyContent: 'center',
    alignItems: 'center',
  },
  testimonialName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimaryContainer,
  },
  testimonialTitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: `${fpColors.onPrimaryContainer}B3`,
  },

  formCol: {
    padding: 20,
    justifyContent: 'center',
  },
  formColDesktop: {
    flex: 1,
    padding: 48,
  },
  formInner: {
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },

  mobileBrand: {
    alignItems: 'center',
    marginBottom: 12,
    gap: 6,
  },
  mobileBrandMark: {
    width: 52,
    height: 52,
    borderRadius: 12,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 4,
  },

  headingBlock: {
    marginBottom: 14,
    alignItems: 'center',
  },
  h1: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 4,
    textAlign: 'center',
  },
  h1Desktop: {
    fontSize: 28,
    textAlign: 'left',
    alignSelf: 'flex-start',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },

  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 14,
    gap: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: fpColors.outlineVariant,
  },
  dividerLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },

  form: {
    gap: 12,
  },
  forgotLink: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.primary,
  },

  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onErrorContainer,
  },

  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: fpColors.primary,
    paddingVertical: 16,
    borderRadius: 12,
    shadowColor: fpColors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 6,
    minHeight: 56,
  },
  ctaDisabled: {
    opacity: 0.7,
  },
  ctaLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 16,
    color: fpColors.onPrimary,
  },

  footerText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    marginTop: 14,
  },
  footerLink: {
    fontFamily: 'Inter_700Bold',
    color: fpColors.primary,
  },

  supportRow: {
    marginTop: 14,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
  },
  supportLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  supportLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.outline,
  },

  blur: {
    position: 'absolute',
    width: 256,
    height: 256,
    borderRadius: 128,
    opacity: 0.3,
  },
  blurTopLeft: {
    top: 80,
    left: -80,
    backgroundColor: fpColors.primary,
  },
  blurBottomRight: {
    bottom: 80,
    right: -80,
    backgroundColor: fpColors.tertiaryContainer,
  },
});
